import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ClipboardCheck,
  HeartPulse,
  TrendingDown,
  Users,
  BarChart3,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

const SONO_STEPS = [
  {
    icon: ClipboardCheck,
    title: "Check in daily",
    desc: "Tap the body map to log where it hurts, rate pain 0–10, then add your scan hours and patient count. The whole check-in takes under 60 seconds.",
  },
  {
    icon: HeartPulse,
    title: "Act on signals",
    desc: "When a body part lights up, Sonote surfaces targeted stretches and ergonomic prompts so you can recover between scans—before pain becomes injury.",
  },
  {
    icon: TrendingDown,
    title: "See your trend",
    desc: "Open Statistics any time to track your personal pain trajectory by region. Your individual data is private—managers only see anonymized clinic-wide trends.",
  },
];

const MANAGER_STEPS = [
  {
    icon: Users,
    title: "Invite your team",
    desc: "Head to Sonographer Management to invite clinicians by email. Each accepts via a private link—no shared logins, no spreadsheets.",
  },
  {
    icon: BarChart3,
    title: "Watch clinic trends",
    desc: "Statistics shows anonymized pain hotspots overlaid with stretching activity. Toggle body parts on or off to focus on what matters this week.",
  },
  {
    icon: ShieldCheck,
    title: "Protect, don't surveil",
    desc: "You see aggregate trends only—individual pain scores stay private to the clinician. Log stretching sessions in the Stretching tab to correlate effort with outcomes.",
  },
];

const STORAGE_PREFIX = "sonote_onboarded_v1_";

export default function WelcomeOnboarding({ user }) {
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const eligible = user && (user.role === "sonographer" || user.role === "clinic_manager");
  const storageKey = eligible ? `${STORAGE_PREFIX}${user.id || user.email}` : null;

  useEffect(() => {
    if (!eligible || !storageKey) return;
    // Server-side flag wins; localStorage is a fast/offline fallback.
    if (user.onboarded_at) return;
    const seen = localStorage.getItem(storageKey);
    if (!seen) {
      setIdx(0);
      setOpen(true);
    }
  }, [eligible, storageKey, user?.onboarded_at]);

  // Allow re-opening via custom event (used by "Show tour again" button)
  useEffect(() => {
    const handler = () => { setIdx(0); setOpen(true); };
    window.addEventListener("sonote:show-tour", handler);
    return () => window.removeEventListener("sonote:show-tour", handler);
  }, []);

  if (!eligible) return null;

  const steps = user.role === "clinic_manager" ? MANAGER_STEPS : SONO_STEPS;
  const total = steps.length;
  const step = steps[idx];
  const Icon = step.icon;
  const isLast = idx === total - 1;

  const dismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, new Date().toISOString());
    setOpen(false);
    // Persist server-side so other devices won't re-prompt. Fire-and-forget.
    if (!user.onboarded_at) {
      api.post("/auth/onboarded")
        .then(({ data }) => { if (data && setUser) setUser(data); })
        .catch(() => { /* localStorage already covers this device */ });
    }
  };

  const next = () => (isLast ? dismiss() : setIdx((i) => i + 1));
  const back = () => setIdx((i) => Math.max(0, i - 1));

  const roleLabel = user.role === "clinic_manager" ? "Clinic Manager" : "Sonographer";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent
        className="max-w-xl p-0 overflow-hidden border-zinc-200"
        data-testid="welcome-onboarding-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="px-8 pt-8 pb-2">
          <span
            className="inline-block text-[11px] tracking-[0.18em] uppercase font-bold px-3 py-1 rounded-full"
            style={{ background: "var(--gold-light)", color: "var(--gold-hover)" }}
            data-testid="welcome-onboarding-role"
          >
            Welcome, {roleLabel}
          </span>
          <h2 className="mt-4 text-2xl font-bold tracking-tight">How Sonote works for you</h2>
        </div>

        <div className="px-8 py-6">
          <div className="flex items-start gap-5" data-testid={`welcome-onboarding-step-${idx + 1}`}>
            <span
              className="shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ background: "var(--gold-light)" }}
            >
              <Icon className="w-7 h-7" style={{ color: "var(--gold-hover)" }} />
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold tracking-[0.18em] text-zinc-400">
                STEP {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </div>
              <h3 className="mt-1 text-xl font-bold">{step.title}</h3>
              <p className="mt-2 text-zinc-600 leading-relaxed text-sm">{step.desc}</p>
            </div>
          </div>

          {/* Progress dots */}
          <div className="flex items-center gap-2 mt-8">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8" : "w-1.5"
                }`}
                style={{ background: i === idx ? "var(--gold)" : "#e4e4e7" }}
              />
            ))}
          </div>
        </div>

        <div className="px-8 py-5 bg-zinc-50 border-t border-zinc-200 flex items-center justify-between">
          <button
            type="button"
            onClick={dismiss}
            className="text-sm text-zinc-500 hover:text-zinc-900 font-medium"
            data-testid="welcome-onboarding-skip"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                type="button"
                onClick={back}
                className="btn-ghost"
                data-testid="welcome-onboarding-back"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="btn-gold"
              data-testid="welcome-onboarding-next"
            >
              {isLast ? "Get started" : "Next"}
              {!isLast && <ArrowRight className="w-4 h-4 ml-2" />}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
